import { useCallback, useMemo } from 'react'
import { Inbox, PlugZap } from 'lucide-react'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import TextField from '../../components/TextField'
import { FormCols, FormSection, FormSpan, InfoNote, SwitchField } from '../../components/Form'
import MailProfilesPanel from './MailProfilesPanel'
import {
  CONNECTION_METHODS,
  deleteImapProfile,
  listImapProfiles,
  saveImapProfile,
  testImapProfile,
} from './phishingService'
import { phishingSettingsStrings as S } from './strings'

const F = S.imapForm

const BLANK = {
  id: 0,
  host: '',
  port: '993',
  username: '',
  password: '',
  folderName: 'INBOX',
  connectionMethod: 'SslOnConnect',
  tls: true,
  deleteCampaignsEmails: false,
  isActive: true,
}

/** `2026-03-04T08:15:00` reads as a date and a time, not as a machine stamp. */
const readableSync = (value) => (value ? String(value).replace('T', ' ').slice(0, 16) : '')

/**
 * إعدادات الوصول إلى الرسائل — the mailbox users forward a suspicious message to.
 *
 * ONE MAILBOX IS READ. The listener service opens the active row and nothing else, so making
 * one active takes the flag off the others — server-side, on save. The list is kept anyway,
 * because a tenant keeps the mailbox it moved away from rather than retyping it.
 *
 * THE FOLDER IS PART OF THE ADDRESS. A mailbox that connects but whose folder cannot be
 * opened reads nothing, which is why the test opens it rather than just signing in.
 */
export default function ImapProfilesPanel() {
  /* Stable identity: the panel reloads whenever `list` changes. */
  const list = useCallback((options) => listImapProfiles(options), [])

  const columns = useMemo(
    () => [
      {
        key: 'host',
        header: S.imap.host,
        value: (row) => `${row.host}:${row.port}`,
        render: (row) => (
          <span dir="ltr">
            {row.host}:{row.port}
          </span>
        ),
      },
      {
        key: 'username',
        header: S.imap.username,
        render: (row) => <span dir="ltr">{row.username}</span>,
      },
      {
        key: 'folderName',
        header: S.imap.folderName,
        render: (row) => <span dir="ltr">{row.folderName}</span>,
      },
      {
        key: 'lastSync',
        header: S.imap.lastSync,
        value: (row) => row.lastSync ?? '',
        render: (row) =>
          row.lastSync ? (
            <span className="ph__date" dir="ltr">
              {readableSync(row.lastSync)}
            </span>
          ) : (
            <span className="ph__muted">{S.imap.neverSynced}</span>
          ),
      },
      {
        key: 'isActive',
        header: S.imap.status,
        value: (row) => (row.isActive ? S.active : S.inactive),
        render: (row) => (
          <StatusBadge tone={row.isActive ? 'success' : 'dark'}>
            {row.isActive ? S.active : S.inactive}
          </StatusBadge>
        ),
      },
    ],
    [],
  )

  const validate = (values) => {
    const found = {}
    if (!values.host.trim()) found.host = S.required
    if (!String(values.port).trim()) found.port = S.required
    if (!values.username.trim()) found.username = S.required
    if (!values.folderName.trim()) found.folderName = S.required
    if (!values.connectionMethod) found.connectionMethod = S.required
    return found
  }

  return (
    <MailProfilesPanel
      stateKey="phishing-imap"
      icon={Inbox}
      columns={columns}
      blank={BLANK}
      labels={{
        add: F.add,
        edit: F.edit,
        subtitle: S.tabs.imap,
        empty: F.empty,
        deleteConfirm: F.deleteConfirm,
        searchPlaceholder: S.imap.host,
      }}
      list={list}
      save={saveImapProfile}
      remove={deleteImapProfile}
      validate={validate}
      test={{ label: F.test, icon: PlugZap, run: (values) => testImapProfile(values) }}
    >
      {({ values, set, errors }) => (
        <>
          <FormSection>
            <FormCols>
              <FormRow label={F.host} error={errors.host} hint={F.hostHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.host}
                    onChange={set('host')}
                    invalid={!!errors.host}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={F.port} error={errors.port} hint={F.portHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    type="number"
                    value={values.port}
                    onChange={set('port')}
                    invalid={!!errors.port}
                  />
                )}
              </FormRow>

              <FormRow label={F.username} error={errors.username}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.username}
                    onChange={set('username')}
                    invalid={!!errors.username}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow
                label={F.password}
                hint={values.id ? F.passwordKeptHint : F.passwordHint}
              >
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    type="password"
                    value={values.password}
                    onChange={set('password')}
                    autoComplete="new-password"
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormRow label={F.folderName} error={errors.folderName} hint={F.folderNameHint}>
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values.folderName}
                    onChange={set('folderName')}
                    invalid={!!errors.folderName}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow
                label={F.connectionMethod}
                error={errors.connectionMethod}
                hint={F.connectionMethodHint}
              >
                {(id) => (
                  <Select
                    id={id}
                    value={values.connectionMethod}
                    onChange={set('connectionMethod')}
                    options={CONNECTION_METHODS}
                    invalid={!!errors.connectionMethod}
                  />
                )}
              </FormRow>

              <FormSpan>
                <SwitchField
                  checked={values.tls}
                  onChange={set('tls')}
                  label={F.tls}
                  hint={F.tlsHint}
                />
              </FormSpan>

              <FormSpan>
                <SwitchField
                  checked={values.deleteCampaignsEmails}
                  onChange={set('deleteCampaignsEmails')}
                  label={F.deleteEmails}
                  hint={F.deleteEmailsHint}
                />
              </FormSpan>

              <FormSpan>
                <SwitchField
                  checked={values.isActive}
                  onChange={set('isActive')}
                  label={F.active}
                  hint={F.activeHint}
                />
              </FormSpan>
            </FormCols>
          </FormSection>

          {values.isActive && <InfoNote icon={Inbox}>{F.singleActiveNote}</InfoNote>}
        </>
      )}
    </MailProfilesPanel>
  )
}
